"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/components/providers/auth-provider";

const REDIRECTED_KEY = "progsity.welcome.redirected";

function alreadyRedirected(userId: string): boolean {
  try {
    return window.sessionStorage.getItem(REDIRECTED_KEY) === userId;
  } catch {
    return false;
  }
}

function rememberRedirected(userId: string): void {
  try {
    window.sessionStorage.setItem(REDIRECTED_KEY, userId);
  } catch {
    // Storage off: the API's `welcomePending` still settles it once the screen is seen.
  }
}

/**
 * `EX-IDN05` (ADR-0088 decision 4): after a new account's first sign-in,
 * wherever it landed (the dashboard after email sign-up, or the page a Google
 * or GitHub sign-in returned to), go to the Welcome screen once and come back
 * to that page afterwards. Never for an impersonation or an issued contest
 * account (the API does not owe them the screen), never twice in one tab even
 * if marking it seen failed, and never on the auth pages or the screen itself.
 */
export function WelcomeRedirect({ locale }: { locale: string }) {
  const { user } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (!user?.welcomePending || user.impersonation || user.accountKind === "contest_temp") return;
    if (/\/(welcome|login|register|forgot-password|reset-password|verify-email)(\/|$)/.test(pathname)) return;
    if (alreadyRedirected(user._id)) return;
    rememberRedirected(user._id);
    const here = `${pathname}${window.location.search}${window.location.hash}`;
    router.replace(`/${locale}/welcome?next=${encodeURIComponent(here)}`);
  }, [user, pathname, locale, router]);

  return null;
}
